import { get, post, del, put } from "aws-amplify/api";
import type {
  IDayUpdate,
  ISuggestedBaseRateUpdate,
  HotelMeta,
} from "@/models/api.models";
import { shoulduseYieldV2ForHotel } from "../utils/featureFlag";

const readJsonBody = async (body: unknown): Promise<unknown> => {
  if (!body || typeof body !== "object") return body;
  if ("json" in body && typeof body.json === "function") {
    return await body.json();
  }
  if ("text" in body && typeof body.text === "function") {
    try {
      return JSON.parse(await body.text());
    } catch {
      return undefined;
    }
  }
  return body;
};

export const YieldApi = {
  getUsersHotelsMeta: async (): Promise<HotelMeta[]> => {
    const { body } = await get({ apiName: "yield", path: "/hotels" }).response;
    const result = await readJsonBody(body);
    if (Array.isArray(result)) return result as HotelMeta[];
    if (
      result &&
      typeof result === "object" &&
      Array.isArray((result as { hotels?: unknown }).hotels)
    ) {
      return (result as { hotels: HotelMeta[] }).hotels;
    }
    return [];
  },

  getSuggestedDays: async (hotelId: number | string): Promise<string[]> => {
    const today = new Date().toISOString().slice(0, 10);
    const { body } = await get({
      apiName: "yield",
      path: `/days/suggested/${hotelId}/${today}/${today}`,
    }).response;

    const result = await readJsonBody(body);
    return Array.isArray(result) ? (result as string[]) : [];
  },

  getAvailability: async (
    hotelId: number,
    startDate: string,
    endDate: string,
  ) => {
    return get({
      apiName: "yield",
      path: `/days/yield/${hotelId}/${startDate}/${endDate}`,
    });
  },

  getFirstPickupData: async (hotelId: number, updatedDate: number) => {
    return get({
      apiName: "yield",
      path: `/lefttosell/${hotelId}/pickup/${updatedDate}/first`,
    });
  },

  getLastPickupData: async (hotelId: number, updatedDate: number) => {
    return get({
      apiName: "yield",
      path: `/lefttosell/${hotelId}/pickup/${updatedDate}/last`,
    });
  },

  publishRatesInView: async (body: IDayUpdate) => {
    const useYieldV2 = shoulduseYieldV2ForHotel(body.hotelId);
    return post({
      apiName: useYieldV2 ? "yieldV2" : "yield",
      path: `/rates/${body.hotelId}/publish`,
      options: { body: { ...body } },
    });
  },

  acceptSuggestedRate: async (body: ISuggestedBaseRateUpdate) => {
    const useYieldV2 = shoulduseYieldV2ForHotel(body.hotelId);
    return post({
      apiName: useYieldV2 ? "yieldV2" : "yield",
      path: `/rates/${body.hotelId}/suggested/${body.start}/${body.end}`,
      options: { body: { ...body } },
    });
  },

  removeSuggestedRate: async (body: ISuggestedBaseRateUpdate) => {
    const useYieldV2 = shoulduseYieldV2ForHotel(body.hotelId);
    return del({
      apiName: useYieldV2 ? "yieldV2" : "yield",
      path: `/rates/${body.hotelId}/suggested/${body.start}/${body.end}`,
      options: {},
    });
  },

  updateSuggestedBaseRate: async (body: ISuggestedBaseRateUpdate) => {
    const useYieldV2 = shoulduseYieldV2ForHotel(body.hotelId);
    return put({
      apiName: useYieldV2 ? "yieldV2" : "yield",
      path: `/rates/${body.hotelId}/suggested/${body.start}/${body.end}`,
      options: { body: { ...body } },
    });
  },
};
